<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    collapseLines?: number;
    lineHeight?: number;
    isShownButton?: boolean;
    buttonText?: string;
  }>(),
  {
    collapseLines: 5,
    lineHeight: 24,
    isShownButton: true,
    buttonText: 'もっと見る',
  }
);

const getLineHeight = (element: HTMLElement): number => {
  const styles = window.getComputedStyle(element);
  if (styles.lineHeight === 'normal') {
    return parseInt(styles.fontSize.split('px')[0], 10) * 1.5;
  }
  return parseInt(styles.lineHeight.split('px')[0], 10);
};

const isShowmore = ref(false);
const paragraphLineHeight = ref(props.lineHeight);
const paragraphHeight = ref(props.lineHeight * props.collapseLines);

const collapseHeight = computed(
  (): number => paragraphLineHeight.value * props.collapseLines
);
const paragraphStyle = computed(
  (): Record<string, string> => ({
    maxHeight: isShowmore.value
      ? `${paragraphHeight.value}px`
      : `${collapseHeight.value}px`,
  })
);
const shouldCollapse = computed(
  (): boolean => paragraphHeight.value > collapseHeight.value
);
const buttonText = computed((): string =>
  isShowmore.value ? '閉じる' : props.buttonText
);

const toggleShowmore = async () => {
  isShowmore.value = !isShowmore.value;
  await nextTick();
  updateParagraphHeight();
};

const paragraph = ref<HTMLElement>();
const updateParagraphHeight = () => {
  if (process.server) return;
  if (!paragraph.value) return;
  paragraphLineHeight.value = getLineHeight(paragraph.value);
  paragraphHeight.value = paragraph.value.scrollHeight;
};

onMounted(() => {
  updateParagraphHeight();
});
</script>

<template>
  <!-- ボタンが下に飛び出す分コンテンツ領域を確保するwrapper -->
  <div
    class="wrapper"
    :class="{
      'wrapper--uncollapse': shouldCollapse && isShowmore,
    }"
  >
    <div class="readmore">
      <div
        ref="paragraph"
        :style="paragraphStyle"
        class="readmore__paragraph"
        :class="{
          'readmore__paragraph--collapse': shouldCollapse && !isShowmore,
        }"
      >
        <slot :is-showmore="isShowmore" />
      </div>
      <button
        v-if="shouldCollapse && isShownButton"
        ref="button"
        class="readmore__button button"
        :class="{
          'readmore__button--collapse': !isShowmore,
        }"
        @click="toggleShowmore"
      >
        <span>{{ buttonText }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$button-height: 32px;
$button-margin: 12px;

.wrapper {
  $root: &;

  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 0;
    transition: height $transition-base;

    @at-root #{$root}--uncollapse#{&} {
      // 6pxは視覚調整
      height: $button-height + $button-margin * 2 + 6px;
    }
  }
}
.readmore {
  position: relative;

  &__paragraph {
    overflow: hidden;
    transition: max-height $transition-base;

    &--collapse {
      mask-image: linear-gradient(
        rgba(0, 0, 0, 1) calc(100% - 80px),
        rgba(0, 0, 0, 0)
      );
    }
  }

  &__button {
    position: absolute;
    left: 50%;
    bottom: -($button-height + $button-margin);
    transform: translateX(-50%);
    transition: bottom $transition-base;

    &:nth-child(n + 2) {
      margin-top: $button-margin;
    }

    &--collapse {
      bottom: $button-margin;
    }
  }
}

.button {
  border: 2px solid $color-border-low-emphasis;
  border-radius: 9999em;
  padding-inline: 12px;
  height: $button-height;
  line-height: 1;
  background-color: $color-surface-primary;
  color: $color-text-and-object-medium-emphasis;
  font-size: 12px;
  font-weight: bold;

  &:focus:not(:focus-visible) {
    outline: none;
  }
}
</style>
